import { CSSProperties } from "react";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClassCategory } from "shared/types/enum";

// Seats taken over capacity, plus the waitlist. Null for a payload without
// the numbers (an older server).
export const capacityText = (schedule: CalendarSchedule) => {
  const { num_of_bookings: taken, capacity } = schedule;
  if (taken == null || capacity == null) return null;
  return {
    seats: `${taken}/${capacity}`,
    full: taken >= capacity,
    waitlist: schedule.waitlist_count ?? 0,
  };
};

const lineStyle: CSSProperties = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontVariantNumeric: "tabular-nums",
};

/**
 * The Kapasitas cell of Daftar Jadwal, worded like the calendar block:
 * "10/10 Penuh · 3 Waitlist" for an open class. A private, duet or triplet
 * shows its seats muted: full is the norm there, and a free spot on a duet
 * still reads as "1/2".
 */
export const CapacityCell = ({
  schedule,
  type,
}: {
  schedule: CalendarSchedule;
  type?: ClassCategory;
}) => {
  const capacity = capacityText(schedule);
  if (!capacity) return <span style={{ color: colors.ink3 }}>-</span>;

  if (type !== ClassCategory.Open)
    return (
      <span style={{ ...lineStyle, color: colors.ink3 }}>{capacity.seats}</span>
    );

  const full = translate("schedule.grid.full");
  const waitlist = translate("schedule.grid.waitlist");
  const title = [
    `${capacity.seats}${capacity.full ? ` ${full}` : ""}`,
    capacity.waitlist ? `${capacity.waitlist} ${waitlist}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span style={lineStyle} title={title}>
      {capacity.seats}
      {/* Ink, not amber: amber text on white is under 4.5:1. The word says it. */}
      {capacity.full && <strong> {full}</strong>}
      {capacity.waitlist > 0 && (
        <>
          <span style={{ color: colors.ink3 }}> · </span>
          {capacity.waitlist}{" "}
          <strong style={{ color: colors.purple }}>{waitlist}</strong>
        </>
      )}
    </span>
  );
};
